import { addSerialNumberToCsv } from "../lib/csv";
import { formatDateValue } from "../lib/dateFormat";
import DateInput from "../components/DateInput";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const numFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const qtyFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const usdFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

// Single-hue measure colour (production weight). Matches the app's existing blue.
const C_WEIGHT = "#2563eb";

const STATUS_LABEL = {
  matched: "Produced at assigned plant",
  produced_at_unassigned_plant: "Produced elsewhere",
  not_produced: "Not started",
};

// Status is always paired with its written label, never carried by colour alone.
const STATUS_STYLE = {
  matched: "bg-emerald-50 text-emerald-800 border-emerald-200",
  produced_at_unassigned_plant: "bg-amber-50 text-amber-800 border-amber-200",
  not_produced: "bg-slate-50 text-slate-600 border-slate-200",
};

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function StatTile({ label, value, sub, tone }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label}
      </div>
      <div
        className={`mt-2 text-2xl font-semibold ${
          tone === "warn" ? "text-amber-700" : "text-slate-900"
        }`}
      >
        {value}
      </div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

export default function ProductionPlantOrderViewDetail() {
  const navigate = useNavigate();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [plant, setPlant] = useState("");
  const [consignee, setConsignee] = useState("");
  const [country, setCountry] = useState("");
  const [matchStatus, setMatchStatus] = useState("");

  const [options, setOptions] = useState({ consignees: [], countries: [] });
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const body = useMemo(
    () => ({
      from_date: fromDate || "",
      to_date: toDate || "",
      plant: plant || "",
      consignee: consignee || "",
      country: country || "",
    }),
    [fromDate, toDate, plant, consignee, country]
  );

  const detailBody = useMemo(
    () => ({ ...body, match_status: matchStatus || "" }),
    [body, matchStatus]
  );

  useEffect(() => {
    let active = true;
    api
      .get("/ceo/filter-options")
      .then((res) => {
        if (!active) return;
        const d = res.data || {};
        setOptions({
          consignees: d.consignees || [],
          countries: d.countries || [],
        });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api
      .post("/ai/ceo-chat/production-plant-order-view", body, { timeout: 180000 })
      .then((res) => {
        if (!active) return;
        setSummary(res.data || null);
      })
      .catch((e) => {
        if (!active) return;
        setSummary(null);
        setError(
          e?.response?.data?.detail ||
            e?.message ||
            "The production plant view could not be loaded."
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [body]);

  useEffect(() => {
    let active = true;
    setDetailLoading(true);
    api
      .post("/ai/ceo-chat/production-plant-order-view/detail", detailBody, {
        timeout: 180000,
      })
      .then((res) => {
        if (!active) return;
        setDetail(res.data || null);
        setPage(1);
      })
      .catch(() => {
        if (!active) return;
        setDetail(null);
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [detailBody]);

  const rows = useMemo(() => detail?.rows || [], [detail]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.PoNumber, r.Consignee, r.Country, r.AssignedPlants, r.ActualPlants]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [rows, search]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pageRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  const exportCsv = useCallback(() => {
    const columns = [
      "PoNumber", "PoDate", "Consignee", "Buyer", "Country",
      "AssignedPlants", "ActualPlants", "MatchStatus",
      "OrderedMcs", "OrderValue", "ProductionWeight", "ProductionRecords",
    ];
    const csv = [
      columns.map(csvEscape).join(","),
      ...filteredRows.map((row) =>
        columns
          .map((c) =>
            c === "MatchStatus"
              ? csvEscape(STATUS_LABEL[row.MatchStatus] || row.MatchStatus)
              : csvEscape(row?.[c])
          )
          .join(",")
      ),
    ].join("\n");
    const blob = new Blob([addSerialNumberToCsv(csv)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "production-plant-order-view.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredRows]);

  const s = summary?.summary || {};
  const plantRows = summary?.rows || [];
  const maxWeight = Math.max(1, ...plantRows.map((r) => r.production_weight || 0));
  const plantsInUse = plantRows.filter((r) => r.orders_assigned > 0);
  const valuePublished = summary?.currency_basis?.value_published;

  const buyersByPlant = useMemo(() => {
    const map = {};
    (summary?.plant_buyer || []).forEach((r) => {
      if (!map[r.plant]) map[r.plant] = [];
      map[r.plant].push(r);
    });
    return map;
  }, [summary]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1700px] space-y-5">
        <div className="flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              Production Plant — Buyer &amp; Order View
            </h1>
            <p className="mt-2 max-w-4xl text-sm text-slate-600">
              Which plant is producing which buyer's orders, and whether that
              matches where the order was assigned to be produced. Assignment
              comes from the order itself; actual production comes from the ERP
              production register.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/ceo-command-center?tab=production")}
            className="shrink-0 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back to Production
          </button>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">From (Order Date)</span>
              <DateInput value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">To (Order Date)</span>
              <DateInput value={toDate} onChange={(e) => setToDate(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Plant</span>
              <select value={plant} onChange={(e) => setPlant(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2">
                <option value="">All Plants</option>
                {plantRows.map((r) => (
                  <option key={r.plant} value={r.plant}>
                    {r.plant}{r.station_name ? ` — ${r.station_name}` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Consignee</span>
              <select value={consignee} onChange={(e) => setConsignee(e.target.value)}
                className="max-w-[240px] rounded-xl border border-slate-300 px-3 py-2">
                <option value="">All Consignees</option>
                {options.consignees.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Destination Country</span>
              <select value={country} onChange={(e) => setCountry(e.target.value)}
                className="max-w-[220px] rounded-xl border border-slate-300 px-3 py-2">
                <option value="">All Countries</option>
                {options.countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">Routing</span>
              <select value={matchStatus} onChange={(e) => setMatchStatus(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2">
                <option value="">All Orders</option>
                <option value="matched">Produced at assigned plant</option>
                <option value="produced_at_unassigned_plant">Produced elsewhere</option>
                <option value="not_produced">Not started</option>
              </select>
            </label>
            <button type="button"
              onClick={() => {
                setFromDate(""); setToDate(""); setPlant("");
                setConsignee(""); setCountry(""); setMatchStatus("");
              }}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Reset Filters
            </button>
          </div>
          <div className="mt-3 text-xs text-slate-500">
            {formatDateValue(summary?.date_range?.from_date)} to {formatDateValue(summary?.date_range?.to_date)}. {" "}
            {summary?.date_range?.note}
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl border bg-white p-8 text-center text-sm text-slate-500 shadow-sm">
            Loading production plant view...
          </div>
        ) : summary ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
              <StatTile label="Confirmed Orders" value={numFmt.format(s.distinct_orders || 0)}
                sub="Distinct orders in this period" />
              <StatTile label="Buyers Served" value={numFmt.format(s.distinct_buyers || 0)}
                sub="Distinct consignees" />
              <StatTile label="Plants in Use" value={numFmt.format(plantsInUse.length)}
                sub="Plants with orders assigned" />
              <StatTile label="Production Recorded"
                value={qtyFmt.format(s.total_production_weight || 0)}
                sub="Production weight against these orders" />
              <StatTile label="Produced Elsewhere"
                value={numFmt.format(s.orders_produced_at_unassigned_plant || 0)}
                tone={(s.orders_produced_at_unassigned_plant || 0) > 0 ? "warn" : undefined}
                sub="Orders produced at an unassigned plant" />
            </div>

            <div className="rounded-2xl border bg-white p-5 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">Plant-wise Order Book &amp; Production</div>
              <div className="mt-1 text-xs text-slate-500">
                Assigned figures are per-plant and non-additive. Production weight is additive.
              </div>
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">Plant</th>
                      <th className="py-2 pr-4">Unit</th>
                      <th className="py-2 pr-4 text-right">Orders Assigned</th>
                      <th className="py-2 pr-4 text-right">Buyers</th>
                      <th className="py-2 pr-4 text-right">Ordered MCS</th>
                      <th className="py-2 pr-4 text-right">Orders Produced</th>
                      <th className="py-2 pr-4 text-right">Production Weight</th>
                      <th className="py-2 pr-4 w-40">Share of Production</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plantRows.map((r) => (
                      <tr key={r.plant} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 pr-4 font-medium text-slate-900 whitespace-nowrap">{r.plant}</td>
                        <td className="py-2 pr-4 text-slate-600 whitespace-nowrap">{r.station_name || "—"}</td>
                        <td className="py-2 pr-4 text-right">{numFmt.format(r.orders_assigned)}</td>
                        <td className="py-2 pr-4 text-right">{numFmt.format(r.buyers_served)}</td>
                        <td className="py-2 pr-4 text-right text-slate-600">{qtyFmt.format(r.ordered_mcs)}</td>
                        <td className="py-2 pr-4 text-right">{numFmt.format(r.orders_produced)}</td>
                        <td className="py-2 pr-4 text-right font-medium text-slate-900">
                          {qtyFmt.format(r.production_weight)}
                        </td>
                        <td className="py-2 pr-4">
                          <div className="h-2 w-full rounded bg-slate-100">
                            <div className="h-2 rounded"
                              style={{
                                width: `${Math.max(0, (r.production_weight / maxWeight) * 100)}%`,
                                background: C_WEIGHT,
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-2xl border bg-white p-5 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">Which Buyers Each Plant Produces For</div>
              <div className="mt-1 text-xs text-slate-500">Top consignees by order count at each plant.</div>
              <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {plantsInUse.map((p) => (
                  <div key={p.plant} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex items-baseline justify-between">
                      <div className="font-medium text-slate-900">{p.plant}</div>
                      <div className="text-xs text-slate-500">{p.station_name}</div>
                    </div>
                    <table className="mt-2 w-full text-xs">
                      <tbody>
                        {(buyersByPlant[p.plant] || []).slice(0, 6).map((b) => (
                          <tr key={b.consignee} className="border-b last:border-0">
                            <td className="py-1 pr-2 text-slate-700">{b.consignee}</td>
                            <td className="py-1 text-right font-medium text-slate-900">
                              {numFmt.format(b.orders)}
                            </td>
                          </tr>
                        ))}
                        {!(buyersByPlant[p.plant] || []).length ? (
                          <tr><td className="py-1 text-slate-400">No orders</td></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Order-level Detail</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {detailLoading
                      ? "Loading orders..."
                      : detail?.truncated
                        ? `Showing ${numFmt.format(filteredRows.length)} of ${numFmt.format(
                            detail.matching_orders || filteredRows.length
                          )} orders — narrow the filters to see them all`
                        : `${numFmt.format(filteredRows.length)} order(s)`}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    placeholder="Search PO, consignee, country, plant"
                    className="w-72 rounded-xl border border-slate-300 px-3 py-2 text-sm" />
                  <button type="button" onClick={exportCsv} disabled={!filteredRows.length}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                    Export CSV
                  </button>
                </div>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">PO Number</th>
                      <th className="py-2 pr-4">Consignee</th>
                      <th className="py-2 pr-4">Country</th>
                      <th className="py-2 pr-4">Order Date</th>
                      <th className="py-2 pr-4">Assigned Plant</th>
                      <th className="py-2 pr-4">Produced At</th>
                      <th className="py-2 pr-4">Routing</th>
                      <th className="py-2 pr-4 text-right">Ordered MCS</th>
                      <th className="py-2 pr-4 text-right">Order Value</th>
                      <th className="py-2 pr-4 text-right">
                        {plant ? `Production Weight (${plant})` : "Production Weight"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((r) => (
                      <tr key={r.PoID} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 pr-4 font-medium text-slate-900 whitespace-nowrap">{r.PoNumber}</td>
                        <td className="py-2 pr-4">{r.Consignee}</td>
                        <td className="py-2 pr-4">{r.Country}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{formatDateValue(r.PoDate) || "—"}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{r.AssignedPlants}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{r.ActualPlants}</td>
                        <td className="py-2 pr-4">
                          <span className={`inline-block rounded-lg border px-2 py-0.5 text-xs ${
                            STATUS_STYLE[r.MatchStatus] || "border-slate-200 bg-slate-50 text-slate-700"}`}>
                            {STATUS_LABEL[r.MatchStatus] || r.MatchStatus}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-right">{qtyFmt.format(r.OrderedMcs || 0)}</td>
                        <td className="py-2 pr-4 text-right">
                          {r.OrderValue == null ? "—" : usdFmt.format(r.OrderValue)}
                        </td>
                        <td className="py-2 pr-4 text-right">{qtyFmt.format(r.ProductionWeight || 0)}</td>
                      </tr>
                    ))}
                    {!pageRows.length && !detailLoading ? (
                      <tr><td className="py-4 text-center text-slate-500" colSpan={10}>
                        No orders match the current filters.
                      </td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              {pageCount > 1 ? (
                <div className="mt-3 flex items-center justify-between text-sm">
                  <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded-xl border border-slate-300 px-3 py-1.5 disabled:opacity-40">Previous</button>
                  <span className="text-slate-600">Page {page} of {pageCount}</span>
                  <button type="button" onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={page >= pageCount}
                    className="rounded-xl border border-slate-300 px-3 py-1.5 disabled:opacity-40">Next</button>
                </div>
              ) : null}

              {detail?.totals ? (
                <div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                  Drill-down totals{detail.truncated
                    ? ` (first ${numFmt.format(detail.totals.orders)} of ${numFmt.format(detail.matching_orders || detail.totals.orders)})`
                    : ""} — {numFmt.format(detail.totals.orders)} orders ·{" "}
                  {numFmt.format(detail.totals.matched)} at assigned plant ·{" "}
                  {numFmt.format(detail.totals.produced_at_unassigned_plant)} elsewhere ·{" "}
                  {numFmt.format(detail.totals.not_produced)} not started ·{" "}
                  {qtyFmt.format(detail.totals.production_weight)} production weight
                  {detail.totals.order_value != null
                    ? ` · ${usdFmt.format(detail.totals.order_value)}`
                    : ""}
                </div>
              ) : null}
            </div>

          </>
        ) : null}
      </div>
    </div>
  );
}
